import React from "react";
import { Link } from "react-router-dom";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { DataTable } from "primereact/datatable";
import { Row } from "primereact/row";
import { TooltipOptions } from "primereact/tooltip/tooltipoptions";
import { CustomSortIcon } from "../../../../components/DatatableHelpers";
import { formatNumber, formatNumberOneDecimal } from "../../shared/reportUtil";
import { WriterDraftScoreModel, PromptScoreModel } from "./PromptUsageByCourseAndPromptViewModel";

export function ScoresByPromptTable(props: {
	className: string;
	courseId: number;
	isConstructedResponse: boolean;
	writerData: WriterDraftScoreModel[];
	classData: PromptScoreModel;
}) {
	const hasData = props.writerData && props.writerData.length > 0;
	const hasAdjustedScoringLevel = props.writerData?.find((d) => d.isScoringLevelAdjusted);

	const studentNameTemplate = (rowData: WriterDraftScoreModel) => {
		return (
			<>
				<Link className="link" to={`/scoreReport?draftId=${rowData.draftId}`}>
					{rowData.writerName}
				</Link>
				{rowData.isScoringLevelAdjusted ? <span className="text-red-700"> *</span> : ""}
			</>
		);
	};

	const textualEvidenceTemplate = (rowData: WriterDraftScoreModel) => {
		return formatNumber(rowData.textualEvidence);
	};

	const contentAccuracyTemplate = (rowData: WriterDraftScoreModel) => {
		return formatNumber(rowData.contentAccuracy);
	};

	const footerGroup = (
		<ColumnGroup>
			<Row>
				<Column footer="Class" footerClassName="font-medium !text-right" />
				<Column footer="" />
				<Column footer={props.classData.essayCount} footerClassName="font-medium !px-2" />
				<Column footer={props.classData.draftCount} footerClassName="font-medium !px-2" />
				<Column
					footer={formatNumberOneDecimal(props.classData.developmentOfIdeas)}
					footerClassName="font-medium !px-2"
					hidden={props.isConstructedResponse}
				/>
				<Column
					footer={formatNumberOneDecimal(props.classData.organization)}
					footerClassName="font-medium !px-2"
					hidden={props.isConstructedResponse}
				/>
				<Column
					footer={formatNumberOneDecimal(props.classData.style)}
					footerClassName="font-medium !px-2"
					hidden={props.isConstructedResponse}
				/>
				<Column
					footer={formatNumberOneDecimal(props.classData.wordChoice)}
					footerClassName="font-medium !px-2"
					hidden={props.isConstructedResponse}
				/>
				<Column
					footer={formatNumberOneDecimal(props.classData.sentenceStructure)}
					footerClassName="font-medium !px-2"
					hidden={props.isConstructedResponse}
				/>
				<Column
					footer={formatNumberOneDecimal(props.classData.conventions)}
					footerClassName="font-medium !px-2"
					hidden={props.isConstructedResponse}
				/>
				<Column
					footer={formatNumberOneDecimal(props.classData.total)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumberOneDecimal(props.classData.textualEvidence)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumberOneDecimal(props.classData.contentAccuracy)}
					footerClassName="font-medium !px-2"
				/>
			</Row>
		</ColumnGroup>
	);

	const tooltipOptions: TooltipOptions = {
		position: "top",
		showDelay: 250,
		hideDelay: 0,
	};

	return (
		<>
			<DataTable
				value={props.writerData}
				dataKey="writerId"
				loading={props.writerData == undefined}
				emptyMessage="No results found"
				stripedRows
				sortIcon={CustomSortIcon}
				className={`block tbl tbl-data tbl-datatable min-w-[40rem] cursor-default ${props.className}`}
				footerColumnGroup={hasData && footerGroup}>
				<Column
					header="Student"
					body={studentNameTemplate}
					sortable
					sortField="writerName"
					headerClassName="!px-2"
					className="!px-2"></Column>
				<Column
					header="Level"
					field="scoringLevel"
					sortable
					headerTooltip="Scoring Level"
					headerTooltipOptions={tooltipOptions}
					headerClassName="!px-2"
					className="!px-2"></Column>
				<Column
					header="Essays"
					field="essayCount"
					sortable
					headerTooltip="Number of Essays"
					headerTooltipOptions={tooltipOptions}
					headerClassName="!px-2"
					className="!px-2"></Column>
				<Column
					header="Drafts"
					field="draftCount"
					sortable
					headerTooltip="Number of Drafts"
					headerTooltipOptions={tooltipOptions}
					headerClassName="!px-2"
					className="!px-2"></Column>
				<Column
					header="Dev"
					field="developmentOfIdeas"
					hidden={props.isConstructedResponse}
					sortable
					headerTooltip="Development of Ideas"
					headerTooltipOptions={tooltipOptions}
					headerClassName="!px-2"
					className="!px-2"></Column>
				<Column
					header="Org"
					field="organization"
					hidden={props.isConstructedResponse}
					sortable
					headerTooltip="Organization"
					headerTooltipOptions={tooltipOptions}
					headerClassName="!px-2"
					className="!px-2"></Column>
				<Column
					header="Style"
					field="style"
					hidden={props.isConstructedResponse}
					sortable
					headerTooltip="Style"
					headerTooltipOptions={tooltipOptions}></Column>
				<Column
					header="WC"
					field="wordChoice"
					hidden={props.isConstructedResponse}
					sortable
					headerTooltip="Word Choice"
					headerTooltipOptions={tooltipOptions}
					headerClassName="!px-2"
					className="!px-2"></Column>
				<Column
					header="Sent"
					field="sentenceStructure"
					hidden={props.isConstructedResponse}
					sortable
					headerTooltip="Sentence Structure"
					headerTooltipOptions={tooltipOptions}
					headerClassName="!px-2"
					className="!px-2"></Column>
				<Column
					header="Conv"
					field="conventions"
					hidden={props.isConstructedResponse}
					sortable
					headerTooltip="Conventions"
					headerTooltipOptions={tooltipOptions}
					headerClassName="!px-2"
					className="!px-2"></Column>
				<Column
					header="Total"
					field="total"
					sortable
					headerTooltip="Total Score"
					headerTooltipOptions={tooltipOptions}
					headerClassName="!px-2"
					className="!px-2"></Column>
				<Column
					header="Text"
					body={textualEvidenceTemplate}
					sortable
					headerTooltip="Textual Evidence"
					headerTooltipOptions={tooltipOptions}
					headerClassName="!px-2"
					className="!px-2"></Column>
				<Column
					header="Cont"
					body={contentAccuracyTemplate}
					sortable
					headerTooltip="Content Accuracy"
					headerTooltipOptions={tooltipOptions}
					headerClassName="!px-2"
					className="!px-2"></Column>
			</DataTable>
			{hasAdjustedScoringLevel && (
				<p className="text-red-700">* Scored on an adjusted scoring level</p>
			)}
		</>
	);
}
